import type { CSSProperties } from 'react'

import type { Column, Header, RowData } from '@tanstack/react-table'

// Default size bounds for a column that declares no minSize/maxSize — shared so
// the resize handler and the fill derivation clamp against the same floor/ceiling.
export const DEFAULT_COLUMN_MIN_SIZE = 20
export const UNCAPPED_COLUMN_SIZE = Number.MAX_SAFE_INTEGER

// Width of the leading checkbox column. ListBandHeader's left padding is derived
// from it so the band checkbox stays aligned with the row checkbox.
export const CHECKBOX_COLUMN_WIDTH = 44

type SharedColumnMeta = {
  align?: 'left' | 'center' | 'right'
  headerClassName?: string
  cellClassName?: string
}

function sanitizeId(id: string): string {
  return id.replace(/[^a-zA-Z0-9_-]/g, '-')
}

function colSizeVar(id: string): string {
  return `--shared-table-column-${sanitizeId(id)}-size`
}

function colStartVar(id: string, pos: 'left' | 'right'): string {
  return `--shared-table-column-${sanitizeId(id)}-${pos}`
}

function headerSizeVar(id: string): string {
  return `--shared-table-header-${sanitizeId(id)}-size`
}

function ref(name: string): string {
  return `var(${name})`
}

function sizeStyle(name: string): CSSProperties {
  const v = ref(name)
  return { width: v, minWidth: v, maxWidth: v }
}

export function getColumnMeta<T extends RowData>(
  column: Column<T, unknown>,
): SharedColumnMeta {
  return (column.columnDef.meta ?? {}) as SharedColumnMeta
}

export function getAlignmentClass(align?: SharedColumnMeta['align']): string {
  switch (align) {
    case 'center':
      return 'tw:justify-center tw:text-center'
    case 'right':
      return 'tw:justify-end tw:text-right'
    default:
      return 'tw:justify-start tw:text-left'
  }
}

export function computeSizeVariables<T extends RowData>(
  visibleColumns: Column<T, unknown>[],
  flatHeaders: Header<T, unknown>[],
): CSSProperties {
  const vars: Record<string, string> = {}

  visibleColumns.forEach(col => {
    vars[colSizeVar(col.id)] = `${col.getSize()}px`
    vars[colStartVar(col.id, 'left')] = `${col.getStart('left')}px`
    vars[colStartVar(col.id, 'right')] = `${col.getAfter('right')}px`
  })

  flatHeaders.forEach(header => {
    vars[headerSizeVar(header.id)] = `${header.getSize()}px`
  })

  return vars as CSSProperties
}

export function getColumnStyle<T extends RowData>(
  column: Column<T, unknown>,
): CSSProperties {
  const pinned = column.getIsPinned()
  const style = sizeStyle(colSizeVar(column.id))

  if (!pinned) {
    return style
  }

  return {
    ...style,
    position: 'sticky',
    left: pinned === 'left' ? ref(colStartVar(column.id, 'left')) : undefined,
    right:
      pinned === 'right' ? ref(colStartVar(column.id, 'right')) : undefined,
    zIndex: 2,
  }
}

function getHeaderPinPosition<T extends RowData>(
  header: Header<T, unknown>,
): 'left' | 'right' | false {
  const positions = [
    ...new Set(
      header
        .getLeafHeaders()
        .map(h => h.column.getIsPinned())
        .filter(Boolean) as ('left' | 'right')[],
    ),
  ]

  return positions.length === 1 ? positions[0] : false
}

export function getHeaderStyle<T extends RowData>(
  header: Header<T, unknown>,
): CSSProperties {
  const leafColumns = header.getLeafHeaders().map(h => h.column)
  const pinned = getHeaderPinPosition(header)
  const style = sizeStyle(headerSizeVar(header.id))

  if (!pinned) {
    return style
  }

  const offsetColumn =
    pinned === 'left' ? leafColumns[0] : leafColumns[leafColumns.length - 1]
  const table = header.getContext().table
  const pinnedCount =
    pinned === 'left'
      ? table.getLeftLeafColumns().length
      : table.getRightLeafColumns().length

  return {
    ...style,
    position: 'sticky',
    left:
      pinned === 'left' ? ref(colStartVar(offsetColumn.id, 'left')) : undefined,
    right:
      pinned === 'right'
        ? ref(colStartVar(offsetColumn.id, 'right'))
        : undefined,
    zIndex: 4 + (pinnedCount - offsetColumn.getPinnedIndex()),
  }
}

export function hasUnsupportedGroupedHeaders<T extends RowData>(
  headerGroups: { headers: Header<T, unknown>[] }[],
): boolean {
  return headerGroups.some(group =>
    group.headers.some(header => {
      if (header.isPlaceholder || header.colSpan <= 1) {
        return false
      }

      const pinStates = [
        ...new Set(
          header.getLeafHeaders().map(h => h.column.getIsPinned() ?? 'center'),
        ),
      ]

      return pinStates.length > 1
    }),
  )
}
